import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, Inject, Input, OnChanges, OnDestroy, Renderer2, SimpleChanges } from '@angular/core';
import { SafeValue } from '@angular/platform-browser';
import { HtmlHelper } from '../html-helper/html-helper';
import { L10N_LOCALE, L10nLocale } from 'angular-l10n';

@Component({
  selector: 'app-rich-content',
  templateUrl: './rich-content.component.html',
  styleUrls: ['./rich-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false
})
export class RichContentComponent implements AfterViewInit, OnDestroy, OnChanges {
  @Input() maxAutoIframeHeight: number = 256;
  @Input() defaultIframeHeight: number = 128;
  @Input() richContent: string;

  iframeHeight: number = this.defaultIframeHeight;
  iframeContentHeight: number = undefined;
  safeRichContent: SafeValue = null;

  get showExpandControls(): boolean {
    const showMoreDiff = (this.iframeContentHeight ?? 0) - (this.iframeHeight ?? 0);
    const showLessDiff = (this.iframeHeight ?? 0) - (this.maxAutoIframeHeight ?? 0);
    if (isNaN(showMoreDiff) && isNaN(showLessDiff)) return false;
    return !this.iframeContentHeight ||
      showMoreDiff > 0 ||
      showLessDiff > 0;
  }

  constructor(
    private htmlHelper: HtmlHelper,
    private renderer2: Renderer2,
    private changeDetector: ChangeDetectorRef,
    @Inject(L10N_LOCALE) public locale: L10nLocale,
  ) { }

  ngAfterViewInit(): void {
    setTimeout(() => this.recalculateHeight(this.getFrame()), 0);
  }

  ngOnDestroy(): void {
    this.removeListener();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.richContent?.currentValue) {
      this.onRichContentChanged();
    }
    if (changes.defaultIframeHeight?.currentValue && !this.iframeContentHeight) {
      this.iframeHeight = changes.defaultIframeHeight.currentValue;
    }
    this.changeDetector.markForCheck();
  }

  onRichContentChanged() {
    // sanitize rich content using HtmlHelper (adds default CSS)
    this.safeRichContent = this.htmlHelper.processRichContent(this.richContent);
    // listen for the onload event of the iframe
    setTimeout(() => this.updateListener(), 0);
  }

  /*
    It's difficult to dynamically size the iframe to fit the its contents (so that
    scrollbars are not required). All of the following helper methods are part of
    the solution to that problem.

    KNOWN ISSUE: this component is not able to resize *down*. If the content height was
    200px and is then changed to 100px, the actual height of the frame remains at 200px.
  */

  /**
   * listen for the onload event from the iframe element and then calculate the size
   */
  removeListener: () => void = () => { };
  updateListener(immediate: boolean = false) {
    if (!this.safeRichContent) {
      this.removeListener();
    } else {
      const iframe = this.getFrame();
      if (iframe) {
        if (immediate) this.recalculateHeight(iframe);
        this.removeListener = this.renderer2.listen(iframe, 'load', _ => {
          setTimeout(() => this.recalculateHeight(iframe), 100);
        });
      } else {
        // if the iframe is obscured (in a non-active tab, for example), try again later
        setTimeout(() => this.updateListener(true), 500);
      }
    }
  }

  /**
   * @returns the iframe element
   */
  getFrame(): HTMLIFrameElement {
    return document.getElementById('rich-content-iframe') as HTMLIFrameElement;
  }

  /**
   * re-calculate the size of the iframe contents when the window is resized
   */
  @HostListener('window:resize', ['$event.target'])
  onResize(_target: never) {
    setTimeout(() => this.setContentHeight(this.getFrame()), 100);
  }

  /**
   * gets the iframes content height and saves it as a member property
   */
  setContentHeight(iframe: HTMLIFrameElement) {
    this.iframeContentHeight = iframe?.contentWindow?.document?.documentElement?.scrollHeight;
    this.changeDetector.markForCheck();
  }

  /**
   * recalculate the height that is used to set the height of the iframe element.
   * constrain to max and min values
   */
  recalculateHeight(iframe: HTMLIFrameElement) {
    this.setContentHeight(iframe);
    this.iframeHeight = Math.min(Math.max(this.iframeContentHeight, this.defaultIframeHeight), this.maxAutoIframeHeight);
    this.changeDetector.markForCheck();
  }

  /**
   * allow the user to manually double the height of the iframe by pressing a button
   */
  expandFrame() {
    let newHeight = this.iframeHeight * 2;
    if (this.iframeContentHeight && newHeight > this.iframeContentHeight) {
      newHeight = this.iframeContentHeight;
    }
    this.iframeHeight = newHeight;
  }

  /**
   * allow the user to manually reset the height of the iframe by pressing a button
   */
  resetFrame() {
    this.iframeHeight = this.maxAutoIframeHeight;
  }
}
